import { UserRole, SubscriptionTier, UserLimits } from "../types";

// Role-based limits configuration
const ROLE_LIMITS: Record<UserRole, UserLimits> = {
  admin: {
    maxMaps: -1, // Unlimited
    maxOverlays: -1, // Unlimited
    canCreatePublicMaps: true,
    canApproveMaps: true,
    canAccessAdminPanel: true,
  },
  user: {
    maxMaps: 20,
    maxOverlays: 20,
    canCreatePublicMaps: false, // Maps are private by default, need approval
    canApproveMaps: false,
    canAccessAdminPanel: false,
  },
  approver: {
    maxMaps: 20,
    maxOverlays: 20,
    canCreatePublicMaps: true,
    canApproveMaps: true,
    canAccessAdminPanel: false,
  },
  public: {
    maxMaps: 0,
    maxOverlays: 0,
    canCreatePublicMaps: false,
    canApproveMaps: false,
    canAccessAdminPanel: false,
  },
};

// Subscription-based limits
const SUBSCRIPTION_LIMITS: Record<SubscriptionTier, Partial<UserLimits>> = {
  free: {
    maxMaps: 20,
    maxOverlays: 20,
  },
  premium: {
    maxMaps: 50,
    maxOverlays: 50,
  },
  enterprise: {
    maxMaps: -1, // Unlimited
    maxOverlays: -1, // Unlimited
  },
};

export class RoleService {
  /**
   * Get user limits based on role and subscription
   */
  static getUserLimits(
    role: UserRole,
    subscriptionTier: SubscriptionTier = "free"
  ): UserLimits {
    const roleLimits = ROLE_LIMITS[role];
    const subscriptionLimits = SUBSCRIPTION_LIMITS[subscriptionTier];

    return {
      ...roleLimits,
      ...subscriptionLimits,
    };
  }

  /**
   * Check if user can create a new map
   */
  static canCreateMap(
    userRole: UserRole,
    currentMapsCount: number,
    subscriptionTier: SubscriptionTier = "free"
  ): boolean {
    const limits = this.getUserLimits(userRole, subscriptionTier);
    return limits.maxMaps === -1 || currentMapsCount < limits.maxMaps;
  }

  /**
   * Check if user can create a new overlay
   */
  static canCreateOverlay(
    userRole: UserRole,
    currentOverlaysCount: number,
    subscriptionTier: SubscriptionTier = "free"
  ): boolean {
    const limits = this.getUserLimits(userRole, subscriptionTier);
    return (
      limits.maxOverlays === -1 || currentOverlaysCount < limits.maxOverlays
    );
  }

  /**
   * Check if user can create public maps
   */
  static canCreatePublicMaps(userRole: UserRole): boolean {
    const limits = this.getUserLimits(userRole);
    return limits.canCreatePublicMaps;
  }

  /**
   * Check if user can approve maps
   */
  static canApproveMaps(userRole: UserRole): boolean {
    const limits = this.getUserLimits(userRole);
    return limits.canApproveMaps;
  }

  /**
   * Check if user can access admin panel
   */
  static canAccessAdminPanel(userRole: UserRole): boolean {
    const limits = this.getUserLimits(userRole);
    return limits.canAccessAdminPanel;
  }

  /**
   * Check if user can access approver panel
   */
  static canAccessApproverPanel(userRole: UserRole): boolean {
    return userRole === "approver" || userRole === "admin";
  }

  /**
   * Check if user is admin
   */
  static isAdmin(userRole: UserRole): boolean {
    return userRole === "admin";
  }

  /**
   * Check if user is approver or admin
   */
  static isApprover(userRole: UserRole): boolean {
    return userRole === "approver" || userRole === "admin";
  }

  /**
   * Check if user is authenticated (not public)
   */
  static isAuthenticated(userRole: UserRole): boolean {
    return userRole !== "public";
  }

  /**
   * Get remaining map slots for user
   */
  static getRemainingMapSlots(
    userRole: UserRole,
    currentMapsCount: number,
    subscriptionTier: SubscriptionTier = "free"
  ): number {
    const limits = this.getUserLimits(userRole, subscriptionTier);
    if (limits.maxMaps === -1) return -1; // Unlimited
    return Math.max(0, limits.maxMaps - currentMapsCount);
  }

  /**
   * Get remaining overlay slots for user
   */
  static getRemainingOverlaySlots(
    userRole: UserRole,
    currentOverlaysCount: number,
    subscriptionTier: SubscriptionTier = "free"
  ): number {
    const limits = this.getUserLimits(userRole, subscriptionTier);
    if (limits.maxOverlays === -1) return -1; // Unlimited
    return Math.max(0, limits.maxOverlays - currentOverlaysCount);
  }
}
